import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import API_BASE_URL from "../config";
import { ArrowLeft, Star, MapPin, MessageCircle, Send, Trash2, UserPlus, UserCheck } from "lucide-react";
import { toast } from "sonner";

const API = API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`;

const BADGE_COLORS = {
  admin: "bg-red-100 text-red-700",
  local_expert: "bg-[#F59E0B]/10 text-[#D97706]",
  active_reviewer: "bg-[#3FAF7A]/10 text-[#3FAF7A]",
  creator: "bg-[#3B82F6]/10 text-[#3B82F6]",
  new_traveler: "bg-[#E2E8E4] text-[#5C6B64]",
};

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function PostDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [following, setFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);

  useEffect(() => { loadPost(); }, [loadPost]);

  // Check if current user already follows the post author
  useEffect(() => {
    if (!user || !post?.author_id || user.id === post.author_id) return;
    const checkFollow = async () => {
      try {
        const { data } = await axios.get(`${API}/users/${post.author_id}`, {
          withCredentials: true,
          headers: authHeaders(),
        });
        const followers = data.followers || [];
        setFollowing(followers.includes(user.id));
      } catch {}
    };
    checkFollow();
  }, [user, post]);

  const loadPost = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/posts/${id}`, { withCredentials: true });
      setPost(data);
    } catch {
      toast.error("Failed to load post");
    } finally {
      setLoading(false);
    }
  }, [id]);

  const handleFollow = async () => {
    if (!user || user === false) { toast.error("Please log in to follow"); return; }
    setFollowLoading(true);
    try {
      if (following) {
        await axios.delete(`${API}/users/${post.author_id}/follow`, {
          withCredentials: true,
          headers: authHeaders(),
        });
        setFollowing(false);
        toast.success(`Unfollowed ${post.author?.name}`);
      } else {
        await axios.post(`${API}/users/${post.author_id}/follow`, {}, {
          withCredentials: true,
          headers: authHeaders(),
        });
        setFollowing(true);
        toast.success(`Now following ${post.author?.name}!`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally {
      setFollowLoading(false);
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    if (!user || user === false) { toast.error("Please log in to comment"); return; }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/posts/${id}/comment`,
        { content: comment.trim() },
        { withCredentials: true, headers: authHeaders() }
      );
      setComment("");
      toast.success("Comment added!");
      loadPost();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add comment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await axios.delete(
        `${API}/posts/${id}/comments/${commentId}`,
        { withCredentials: true, headers: authHeaders() }
      );
      toast.success("Comment deleted");
      loadPost();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete comment");
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-48" />
          <div className="h-64 bg-muted rounded-2xl" />
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8 text-center">
        <p className="text-muted-foreground">Post not found</p>
        <Link to="/channels" className="text-primary font-medium mt-3 inline-block hover:underline">
          Back to Community
        </Link>
      </div>
    );
  }

  const isOwnPost = user && post.author_id === user.id;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
      <Link to={`/channels/${post.channel_id}`}
        className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground text-sm mb-6 transition-colors">
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to channel
      </Link>

      {/* ── Post Card ─────────────────────────────────────────────── */}
      <div className="bg-card border border-border rounded-2xl p-5 sm:p-6 mb-6">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className="px-2.5 py-0.5 rounded-full text-xs font-bold uppercase bg-primary/10 text-primary border border-primary/20">
            {post.type}
          </span>
          {post.rating && (
            <span className="flex items-center gap-0.5 text-xs text-[#F59E0B]">
              <Star className="w-3 h-3 fill-current" /> {post.rating}/5
            </span>
          )}
          {post.place && (
            <span className="flex items-center gap-0.5 text-xs text-muted-foreground">
              <MapPin className="w-3 h-3" strokeWidth={1.5} /> {post.place.name}
            </span>
          )}
        </div>

        <h1 className="text-xl sm:text-2xl font-bold text-foreground mb-3">{post.title}</h1>
        <p className="text-foreground leading-relaxed whitespace-pre-line mb-4 text-sm sm:text-base">{post.content}</p>

        {/* Media */}
        {post.media_urls?.length > 0 && (
          <div className={`grid gap-2 mb-4 ${post.media_urls.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}>
            {post.media_urls.map((url, i) => {
              const isVideo = url.match(/\.(mp4|mov|webm|avi)/i) || url.includes("/video/");
              return isVideo ? (
                <video key={i} src={url} controls className="w-full rounded-xl border border-border max-h-80 object-cover" />
              ) : (
                <img key={i} src={url} alt={`media-${i}`} className="w-full rounded-xl border border-border object-cover max-h-80" />
              );
            })}
          </div>
        )}

        {/* Author row with Follow button */}
        <div className="flex items-center gap-2 pt-4 border-t border-border flex-wrap">
          <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-bold shrink-0">
            {post.author?.name?.charAt(0)?.toUpperCase() || "U"}
          </div>
          <span className="text-sm font-medium text-foreground">{post.author?.name || "Unknown"}</span>
          {post.author?.badges?.filter(b => b !== "new_traveler").map(b => (
            <span key={b} className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${BADGE_COLORS[b] || BADGE_COLORS.new_traveler}`}>
              {b.replace(/_/g, " ")}
            </span>
          ))}

          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {new Date(post.created_at).toLocaleDateString()}
            </span>
            {/* Follow button — only show if viewing someone else's post and logged in */}
            {user && user !== false && !isOwnPost && (
              <button
                onClick={handleFollow}
                disabled={followLoading}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all border ${
                  following
                    ? "bg-background text-muted-foreground border-border hover:bg-destructive/10 hover:text-destructive hover:border-destructive/30"
                    : "bg-primary text-primary-foreground border-primary hover:bg-primary/90"
                } disabled:opacity-50`}
              >
                {following
                  ? <><UserCheck className="w-3.5 h-3.5" /> Following</>
                  : <><UserPlus className="w-3.5 h-3.5" /> Follow</>
                }
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Comments ─────────────────────────────────────────────── */}
      <div className="bg-card border border-border rounded-2xl p-5 sm:p-6">
        <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <MessageCircle className="w-5 h-5" strokeWidth={1.5} />
          Comments ({post.comments?.length || 0})
        </h2>

        {user && user !== false ? (
          <form onSubmit={handleComment} className="flex gap-2 mb-6">
            <input value={comment} onChange={(e) => setComment(e.target.value)}
              placeholder="Write a comment..."
              className="flex-1 bg-background border border-border rounded-xl px-4 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors min-w-0" />
            <button type="submit" disabled={submitting || !comment.trim()}
              className="bg-primary text-primary-foreground rounded-xl px-3 sm:px-4 py-2.5 text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-1.5 shrink-0">
              <Send className="w-4 h-4" strokeWidth={1.5} />
              <span className="hidden sm:inline">{submitting ? "..." : "Send"}</span>
            </button>
          </form>
        ) : (
          <div className="mb-6 p-3 bg-background rounded-xl text-sm text-muted-foreground text-center">
            <Link to="/login" className="text-primary font-medium hover:underline">Log in</Link> to comment
          </div>
        )}

        <div className="space-y-4">
          {post.comments?.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No comments yet. Be the first!</p>
          ) : (
            post.comments?.map((c) => (
              <div key={c.id} className="flex gap-3">
                <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-bold shrink-0">
                  {c.author?.name?.charAt(0)?.toUpperCase() || "U"}
                </div>
                <div className="flex-1 bg-background rounded-xl px-4 py-3 min-w-0 border border-border">
                  <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-medium text-foreground truncate">{c.author?.name || "Unknown"}</span>
                      <span className="text-xs text-muted-foreground shrink-0">{new Date(c.created_at).toLocaleDateString()}</span>
                    </div>
                    {user && (user.id === c.author_id || user._id === c.author_id || user.role === "admin") && (
                      <button onClick={() => handleDeleteComment(c.id)}
                        className="text-muted-foreground hover:text-destructive transition-colors p-0.5 shrink-0"
                        title="Delete comment">
                        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </button>
                    )}
                  </div>
                  <p className="text-sm text-foreground break-words">{c.content}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
